import DOMPurify from 'dompurify';

/**
 * Sanitiza HTML antes de meterlo con `dangerouslySetInnerHTML`.
 *
 * Las condiciones de venta se guardan como HTML: las escribe la propia cabaña
 * desde su panel con el editor Tiptap. Es tentador confiar en eso —el que
 * escribe es un rol 1/2 del tenant, no un desconocido— pero esa confianza vive
 * en el CLIENTE QUE ESCRIBE, no en el sistema. Es la misma discusión que el
 * piso de contraseñas: si el mínimo lo pone el formulario y no el backend, no
 * es una regla, es una sugerencia.
 *
 * Y el riesgo no es teórico: las condiciones se renderizan en el **micrositio
 * público** de la cabaña, así que el markup termina corriendo en el navegador
 * de cualquier visitante del remate.
 *
 * `utils/streaming.js` ya seguía este criterio para el embed de streaming
 * ("meter markup ajeno con dangerouslySetInnerHTML es un XSS servido en
 * bandeja"); esto lo extiende a las condiciones.
 *
 * Se sanitiza al RENDERIZAR y no al guardar a propósito: así queda cubierto
 * también todo el HTML que ya está guardado en la base desde antes.
 *
 * La whitelist es la que puede producir Tiptap con la config del panel
 * (starter-kit + underline + text-align + color/text-style). Nada de scripts,
 * iframes, handlers `on*` ni `style` arbitrario.
 */
const CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'hr', 'div', 'span',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'mark', 'sub', 'sup',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  // `style` entra porque Tiptap lo usa para el color y la alineación. DOMPurify
  // parsea el atributo con su propio sanitizador de CSS y descarta lo peligroso
  // (`expression()`, `url(javascript:...)`), así que no hace falta —ni existe—
  // una whitelist de propiedades: esa opción es de `sanitize-html`, no de esta
  // librería, y ponerla acá quedaría ignorada en silencio.
  ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'colspan', 'rowspan', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
};

export const htmlSeguro = (html) => DOMPurify.sanitize(String(html ?? ''), CONFIG);

/**
 * Azúcar para el caso de uso real: devuelve LAS PROPS del div, no el objeto de
 * adentro, así en el JSX se lee de un saque que va sanitizado.
 *
 *   <div {...htmlSanitizado(seccion.contenido)} />
 *
 * ⚠️ DEVUELVE `{ dangerouslySetInnerHTML: { __html } }` Y NO `{ __html }`, y no
 * es un detalle de estilo: los TRES llamadores la spreadean sobre un `<div>`
 * —es el idiom que documenta este mismo comentario— y con `{ __html }` pelado
 * React no ve `dangerouslySetInnerHTML` por ningún lado: ve una prop
 * desconocida y la escribe como ATRIBUTO. El HTML de las condiciones terminaba
 * DENTRO de la etiqueta (`<div __html="<p>FINANCIACIÓN...">`) en vez de ser el
 * contenido, así que la página salía en blanco: ni el texto, ni el cartel de
 * "no hay información" —que no aparece porque `contenido` SÍ tiene dato—.
 * Falla en silencio: no hay error de JS, no hay request fallido, y el
 * sanitizado anda perfecto (medido: entran 1776 caracteres y salen 1776).
 * Se lo comió el micrositio público de los remates, el modal de condiciones
 * previo a la primera oferta y "Mis ofertas" del panel del comprador.
 */
export const htmlSanitizado = (html) => ({
  dangerouslySetInnerHTML: { __html: htmlSeguro(html) },
});
